import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, RefreshCw, Search, AlertTriangle, CheckCircle, XCircle, User, LogIn, UserPlus, FileText, Filter } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function LogsActividad() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchEmail, setSearchEmail] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [filterSuccess, setFilterSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    fetchLogs();
    fetchStats();
  }, [filterAction, filterSuccess]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      let url = `${API_URL}/api/admin/activity-logs?limit=200`;
      if (filterAction) url += `&action=${filterAction}`;
      if (filterSuccess !== '') url += `&success=${filterSuccess}`;
      if (searchEmail) url += `&email=${encodeURIComponent(searchEmail)}`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Error al cargar logs');

      const data = await response.json();
      setLogs(data.logs || []);
    } catch (error) {
      toast.error('Error al cargar los logs');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/activity-logs/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Error al cargar estadísticas');

      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleSearch = () => {
    fetchLogs();
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'LOGIN':
      case 'LOGIN_FAILED':
        return <LogIn className="h-4 w-4" />;
      case 'REGISTER':
        return <UserPlus className="h-4 w-4" />;
      case 'VERIFY':
      case 'VERIFY_FAILED':
        return <CheckCircle className="h-4 w-4" />;
      case 'POSTULACION':
        return <FileText className="h-4 w-4" />;
      default:
        return <User className="h-4 w-4" />;
    }
  };

  const getActionColor = (action, success) => {
    if (!success) return 'bg-red-100 text-red-800';
    switch (action) {
      case 'LOGIN':
        return 'bg-blue-100 text-blue-800';
      case 'REGISTER':
        return 'bg-green-100 text-green-800';
      case 'VERIFY':
        return 'bg-purple-100 text-purple-800';
      case 'POSTULACION':
        return 'bg-amber-100 text-amber-800';
      default:
        return 'bg-slate-100 text-slate-800';
    }
  };

  const getActionLabel = (action) => {
    const labels = {
      'LOGIN': 'Inicio de Sesión',
      'LOGIN_FAILED': 'Login Fallido',
      'REGISTER': 'Registro',
      'VERIFY': 'Verificación',
      'VERIFY_FAILED': 'Verificación Fallida',
      'POSTULACION': 'Postulación'
    };
    return labels[action] || action;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('es-CO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderTabContent = () => {
    if (activeTab === 'failed' && stats) {
      return (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-red-700 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" />
            Intentos de Login Fallidos Recientes
          </h3>
          {stats.failed_logins?.length === 0 ? (
            <p className="text-slate-500 text-center py-8">No hay intentos fallidos recientes</p>
          ) : (
            <div className="space-y-2">
              {stats.failed_logins?.map((log, idx) => (
                <div key={idx} className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium text-red-800">{log.email}</p>
                      <p className="text-sm text-red-600">{log.details?.error}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-slate-500">{formatDate(log.timestamp)}</p>
                      {log.ip_address && (
                        <p className="text-xs text-slate-400">IP: {log.ip_address}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    if (activeTab === 'registros' && stats) {
      return (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-green-700 flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            Registros Recientes
          </h3>
          {stats.recent_registrations?.length === 0 ? (
            <p className="text-slate-500 text-center py-8">No hay registros recientes</p>
          ) : (
            <div className="space-y-2">
              {stats.recent_registrations?.map((log, idx) => (
                <div key={idx} className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium text-green-800">{log.email}</p>
                      <p className="text-sm text-green-600">
                        {log.details?.name} - {log.details?.user_type}
                      </p>
                      {log.details?.document_id && (
                        <p className="text-xs text-slate-500">Doc: {log.details.document_id}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-slate-500">{formatDate(log.timestamp)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    if (activeTab === 'postulaciones' && stats) {
      return (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-amber-700 flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Postulaciones Recientes
          </h3>
          {stats.recent_postulaciones?.length === 0 ? (
            <p className="text-slate-500 text-center py-8">No hay postulaciones recientes</p>
          ) : (
            <div className="space-y-2">
              {stats.recent_postulaciones?.map((log, idx) => (
                <div key={idx} className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium text-amber-800">{log.email}</p>
                      <p className="text-sm text-amber-600">
                        Convocatoria: {log.details?.convocatoria_nombre}
                      </p>
                      <p className="text-xs text-slate-500">
                        Tipo: {log.details?.tipo_postulante}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-slate-500">{formatDate(log.timestamp)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    // All logs tab
    return (
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Fecha/Hora</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Acción</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Email</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Detalles</th>
              <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600">Estado</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">IP</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {logs.map((log, idx) => (
              <tr key={idx} className="hover:bg-slate-50">
                <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap">
                  {formatDate(log.timestamp)}
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${getActionColor(log.action, log.success)}`}>
                    {getActionIcon(log.action)}
                    {getActionLabel(log.action)}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm font-medium text-slate-900">
                  {log.email || '-'}
                </td>
                <td className="px-4 py-3 text-sm text-slate-600 max-w-xs truncate">
                  {log.details?.error || log.details?.name || log.details?.convocatoria_nombre || '-'}
                </td>
                <td className="px-4 py-3 text-center">
                  {log.success ? (
                    <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-500 mx-auto" />
                  )}
                </td>
                <td className="px-4 py-3 text-xs text-slate-400">
                  {log.ip_address || '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {logs.length === 0 && (
          <p className="text-center py-8 text-slate-500">No hay logs que coincidan con los filtros</p>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-8" data-testid="logs-actividad-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate('/dashboard')}
            className="p-2 hover:bg-slate-200 rounded-lg transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Logs de Actividad</h1>
            <p className="text-slate-500 text-sm">Registro de eventos del sistema</p>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div 
              className={`bg-white rounded-xl shadow-sm p-4 border-l-4 cursor-pointer transition-all ${activeTab === 'all' ? 'border-blue-500 ring-2 ring-blue-200' : 'border-blue-300 hover:border-blue-500'}`}
              onClick={() => setActiveTab('all')}
            >
              <p className="text-sm text-slate-500">Total Logs</p>
              <p className="text-2xl font-bold text-blue-600">{logs.length}</p>
            </div>
            <div 
              className={`bg-white rounded-xl shadow-sm p-4 border-l-4 cursor-pointer transition-all ${activeTab === 'failed' ? 'border-red-500 ring-2 ring-red-200' : 'border-red-300 hover:border-red-500'}`}
              onClick={() => setActiveTab('failed')}
            >
              <p className="text-sm text-slate-500">Logins Fallidos</p>
              <p className="text-2xl font-bold text-red-600">{stats.failed_logins?.length || 0}</p>
            </div>
            <div 
              className={`bg-white rounded-xl shadow-sm p-4 border-l-4 cursor-pointer transition-all ${activeTab === 'registros' ? 'border-green-500 ring-2 ring-green-200' : 'border-green-300 hover:border-green-500'}`}
              onClick={() => setActiveTab('registros')}
            >
              <p className="text-sm text-slate-500">Registros Recientes</p>
              <p className="text-2xl font-bold text-green-600">{stats.recent_registrations?.length || 0}</p>
            </div>
            <div 
              className={`bg-white rounded-xl shadow-sm p-4 border-l-4 cursor-pointer transition-all ${activeTab === 'postulaciones' ? 'border-amber-500 ring-2 ring-amber-200' : 'border-amber-300 hover:border-amber-500'}`}
              onClick={() => setActiveTab('postulaciones')}
            >
              <p className="text-sm text-slate-500">Postulaciones</p>
              <p className="text-2xl font-bold text-amber-600">{stats.recent_postulaciones?.length || 0}</p>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="text"
                  value={searchEmail}
                  onChange={(e) => setSearchEmail(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  placeholder="Buscar por email..."
                  className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  data-testid="search-email-input"
                />
              </div>
            </div>
            <select
              value={filterAction}
              onChange={(e) => setFilterAction(e.target.value)}
              className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              data-testid="filter-action-select"
            >
              <option value="">Todas las acciones</option>
              <option value="LOGIN">Inicio de Sesión</option>
              <option value="LOGIN_FAILED">Login Fallido</option>
              <option value="REGISTER">Registro</option>
              <option value="VERIFY">Verificación</option>
              <option value="VERIFY_FAILED">Verificación Fallida</option>
              <option value="POSTULACION">Postulación</option>
            </select>
            <select
              value={filterSuccess}
              onChange={(e) => setFilterSuccess(e.target.value)}
              className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              data-testid="filter-success-select"
            >
              <option value="">Todos los estados</option>
              <option value="true">Exitosos</option>
              <option value="false">Fallidos</option>
            </select>
            <button
              onClick={handleSearch}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
              data-testid="search-button"
            >
              <Search className="h-4 w-4" />
              Buscar
            </button>
            <button
              onClick={() => { fetchLogs(); fetchStats(); }}
              className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 flex items-center gap-2"
              data-testid="refresh-button"
            >
              <RefreshCw className="h-4 w-4" />
              Refrescar
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-xl shadow-sm">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
            </div>
          ) : (
            <div className="p-4">
              {renderTabContent()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
